import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Global from '../../../global';
import GTMLayer from '../../../facades/gtm-layer-facade';
import { KambiWidgetActions } from '../../../redux/kambi-widget';
import { changeOddsFormat } from '../../../redux/user/user-actions';
import WidgetApi from '../../../services/kambi-widget-api/index';
import { eActionsGA, eOddsFormats } from '../../../utils/enums';
import {
  WIDGET_API_TYPE_COMBINATION,
  WIDGET_API_UPDATE_APPEND
} from '../../../utils/constants';

class KambiWidgets extends React.Component {
  /**
   * Kambi Callback - Get bet status.
   * @typedef {object} KambiCallbacks
   * @property {GetBetStatus} getBetStatus - Get bet status.
   * @property {GetCurrentBetSlipOutcomes} getCurrentBetSlipOutcomes - Get current bet slip outcomes.
   * @property {ClientOddsFormatChanged} clientOddsFormatChanged - Client odds format changed.
   * @property {ClearQueuedOutcomes} clearQueuedOutcomes - Clear queued outcomes.
   * @property {NavigateClient} navigateClient - Navigate client.
   */
  callBackFunction = {
    /**
     * Kambi Callback - Get bet status.
     * @callback GetBetStatus
     * @param {string} betStatus - Bet status.
     * @returns {void}
     */
    getBetStatus: betStatus => {
      Global.logger.info(`[KambiWidgets] Bet status => ${betStatus}`);
    },

    /**
     * Kambi Callback - Get current bet slip outcomes.
     * @callback GetCurrentBetSlipOutcomes
     * @param {{outcomes: Object}} betSlipData - Bet slip data.
     * @returns {void}
     */
    getCurrentBetSlipOutcomes: betSlipData => {
      Global.logger.info(
        `[KambiWidgets] Current betslip outcomes  ${JSON.stringify(
          betSlipData.outcomes
        )}`
      );
      this.props.dispatch(
        KambiWidgetActions.setLatestOutcomes(betSlipData.outcomes)
      );
    },

    /**
     * Kambi Callback - Client odds format changed.
     * @callback ClientOddsFormatChanged
     * @param {string} newOddsFormat - New odds format.
     * @returns {void}
     */
    clientOddsFormatChanged: newOddsFormat => {
      Global.logger.info(
        `[KambiWidgets] Client odds format changed ${newOddsFormat}`
      );

      const knownOddsFormats = Object.values(eOddsFormats);
      const isKnownOddsFormat = knownOddsFormats.includes(newOddsFormat);
      if (isKnownOddsFormat) {
        this.props.dispatch(changeOddsFormat(newOddsFormat));
      } else {
        Global.logger.info(
          `[KambiWidgets] Client odds format changed to unknown odds format => ${newOddsFormat}`
        );
      }
    },

    /**
     * Kambi Callback - Clear queued outcomes.
     * @callback ClearQueuedOutcomes
     * @returns {void}
     */
    clearQueuedOutcomes: () => {
      const { queuedOutcomes, dispatch } = this.props;
      const queuedOutcomeIds = Object.keys(queuedOutcomes);
      if (Object.keys(queuedOutcomeIds).length) {
        Global.logger.info(
          `[KambiWidgets] Setting queued outcomes to betslip and clear queue ${JSON.stringify(
            queuedOutcomeIds
          )}`
        );

        WidgetApi.get().setOutcomesToBetSlip(
          queuedOutcomeIds,
          WIDGET_API_UPDATE_APPEND,
          WIDGET_API_TYPE_COMBINATION
        );

        dispatch(KambiWidgetActions.setLatestOutcomes(queuedOutcomeIds));
        dispatch(KambiWidgetActions.clearQueuedOutcomes());
        const outcomesDetails = Object.values(queuedOutcomes);
        outcomesDetails.forEach(outcome => {
          const { eventName, gaCategory } = outcome;
          GTMLayer.sendEventToGA(
            gaCategory,
            eActionsGA.AddToBetslip,
            eventName
          );
        });
      }
    },

    /**
     * Kambi Callback - Navigate client.
     * @callback NavigateClient
     * @returns {void}
     */
    navigateClient: () => {
      const { dispatch } = this.props;
      dispatch(
        KambiWidgetActions.clientInitSucceeded(
          'kambi-widgets-navigate-callback'
        )
      );
    }
  };

  shouldComponentUpdate() {
    return false;
  }

  componentDidMount() {
    const { useProdApis, userContext } = this.props;

    WidgetApi.init(this.callBackFunction, useProdApis, userContext);
    if (!Global.isProduction) {
      window.testWidget = WidgetApi.get();
    }
  }

  render() {
    return <div id="kambiWidgetApi"></div>;
  }
}

KambiWidgets.propTypes = {
  dispatch: PropTypes.func.isRequired,
  queuedOutcomes: PropTypes.object,
  useProdApis: PropTypes.bool,
  userContext: PropTypes.object
};

function mapStateToProps(state) {
  const {
    appConfig: { useProdApis },
    kambiWidget: { queuedOutcomes },
    userContext
  } = state;

  return { queuedOutcomes, useProdApis, userContext };
}

export default connect(mapStateToProps)(KambiWidgets);
